import React from 'react';
import { CheckIcon, CompassIcon, CoinsIcon, EyeIcon, LayersIcon, TrendingUpIcon } from 'lucide-react';
import { ECONOMICS_POINTS, FOOTNOTE, FORMAT_LOGIC, GOVERNANCE_POINTS, KEY_POINTS, LIFECYCLE_PRIORITIES, NEXT_STEPS, OPERATING_PROOF, PAPER_META, SNAK_PAYOFFS, SNAK_STEPS, VALUE_LOGIC } from '../data/conceptPaper';
const SNAKKER_LOGO = 'https://cdn.magicpatterns.com/uploads/98Gnmk6tUobFbbxiaTEqD8/image.png';
interface SectionProps {
  number: string;
  title: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  children: React.ReactNode;
  breakBefore?: boolean;
}
function Section({
  number,
  title,
  icon: Icon,
  children,
  breakBefore = false
}: SectionProps) {
  return <section className={`mt-10 ${breakBefore ? 'paper-break' : ''}`} aria-labelledby={`paper-section-${number}`} data-id="element-9" data-mp="3jjgo3tvjeto9">
      <div className="mb-4 flex items-center gap-3 border-b border-black/10 pb-3" data-id="element-10" data-mp="12y8t1g171l6c">
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-11" data-mp="ywvln1igd9xb">
          <Icon className="h-4 w-4" data-id="element-12" data-mp="1hzl83oybcznz" />
        </span>
        <p className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-13" data-mp="1hy0bzuwcp8o0">
          {number}
        </p>
        <h2 id={`paper-section-${number}`} className="text-[1.35rem] font-extrabold leading-tight tracking-[-0.02em] text-[var(--fg)]" data-id="element-14" data-mp="1lzltjtjtyb4j">
          {title}
        </h2>
      </div>
      {children}
    </section>;
}
function Paragraph({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="mt-3 text-[0.95rem] leading-[1.7] text-[var(--fg)]/85" data-id="element-15" data-mp="13afa24b7pfho">
      {children}
    </p>;
}

/**
 * Executive-summary concept paper. Rendered inside a dialog in the deck and
 * printed to A4 portrait via the paper print styles in index.css.
 */
export function ConceptPaper() {
  return <article className="paper-sheet mx-auto w-full max-w-[52rem] bg-white px-[clamp(1.5rem,4vw,3.5rem)] py-[clamp(2rem,4vw,3.5rem)] text-[var(--fg)] shadow-xl ring-1 ring-black/5" data-id="element-16" data-mp="fkuorerl6658">
      {/* Masthead */}
      <header className="paper-avoid-break border-b-2 border-[var(--brand)] pb-6" data-id="element-17" data-mp="1dxp3gw7534uh">
        <div className="flex items-start justify-between gap-6" data-id="element-18" data-mp="1roc3qssjt2so">
          <div data-id="element-19" data-mp="1rqok51inr53m">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.22em] text-[var(--brand)]" data-id="element-20" data-mp="1rqewhkl35a13">
              {PAPER_META.eyebrow}
            </p>
            <h1 className="mt-3 max-w-xl text-[clamp(1.8rem,3.4vw,2.6rem)] font-extrabold leading-[1.05] tracking-[-0.035em]" data-id="element-21" data-mp="zy7bk7yjrbgw">
              {PAPER_META.title}
            </h1>
          </div>
          <img src={SNAKKER_LOGO} alt="Snakker" className="h-8 w-auto flex-shrink-0 object-contain" data-id="element-22" data-mp="18z5gqtkq1p12" />
        </div>

        <p className="mt-5 max-w-2xl text-[1rem] font-semibold leading-[1.6] text-[var(--fg)]/80" data-id="element-23" data-mp="2kd9e89qipvav">
          {PAPER_META.standfirst}
        </p>
        <p className="mt-4 text-[0.7rem] font-bold uppercase tracking-[0.16em] text-[var(--muted)]" data-id="element-24" data-mp="2fdd7w3uclrs5">
          {PAPER_META.readingTime}
        </p>
      </header>

      {/* The one-minute version */}
      <section className="paper-avoid-break mt-8 rounded-2xl bg-[var(--surface)] p-[clamp(1.25rem,2.5vw,2rem)]" aria-labelledby="paper-summary" data-id="element-25" data-mp="2rxjvjykqu0df">
        <h2 id="paper-summary" className="text-[0.7rem] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-26" data-mp="2ivkfp9lgt2c2">
          The one-minute version
        </h2>
        <dl className="mt-4 space-y-3" data-id="element-27" data-mp="3bkhw17648p26">
          {KEY_POINTS.map(point => <div key={point.label} className="flex gap-3" data-id="element-28" data-mp="3bmknzwqtws9j">
              <span className="mt-[0.45rem] flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-white" data-id="element-29" data-mp="2wvbzxs2kreyi">
                <CheckIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-30" data-mp="3pkiz8t9us8kt" />
              </span>
              <div className="min-w-0" data-id="element-31" data-mp="3piprtbrf1kbr">
                <dt className="text-[0.9rem] font-extrabold leading-snug" data-id="element-32" data-mp="3ucaq74wx8gbh">
                  {point.label}
                </dt>
                <dd className="text-[0.9rem] leading-[1.6] text-[var(--muted)]" data-id="element-33" data-mp="25qzipymxb45o">
                  {point.text}
                </dd>
              </div>
            </div>)}
        </dl>
      </section>

      {/* 01 — The problem */}
      <Section number="01" title="HCPs don’t lack content. They lack time." icon={CompassIcon} data-id="element-34" data-mp="1rpwxfo3r5xvt">
        <Paragraph data-id="element-35" data-mp="2pdfdzl3no4ad">
          Every brand team is producing more material than its audience can
          possibly absorb. The bottleneck has moved: it is no longer content
          production, approval or distribution — it is the few minutes of
          genuine attention an HCP has left in a working day.
        </Paragraph>
        <Paragraph data-id="element-36" data-mp="2apasyah4rgof">
          Long-form assets are built on the assumption that the reader will make
          time. Most do not. What gets finished is short, relevant and worth the
          interruption — and what gets finished is the only thing that can
          change behaviour or produce data.
        </Paragraph>
      </Section>

      {/* 02 — What a Snak is */}
      <Section number="02" title="A Snak is a short story you participate in" icon={LayersIcon} data-id="element-37" data-mp="3fn64elnmta4y">
        <Paragraph data-id="element-38" data-mp="3kmitld1z6tap">
          A Snak turns complex, already-approved content into a short, engaging,
          gamified journey. HCPs do not just read or watch — they choose,
          predict, react and discover. Every interaction creates insight.
        </Paragraph>

        <ol className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-39" data-mp="3tl4h7mmb0yic">
          {SNAK_STEPS.map(step => <li key={step.step} className="paper-avoid-break rounded-xl border border-black/10 p-4" data-id="element-40" data-mp="2ejirhtx6oba">
              <p className="flex items-baseline gap-2" data-id="element-41" data-mp="c6z2ajkv7kya">
                <span className="text-[0.6rem] font-bold tracking-[0.18em] text-[var(--muted)]" data-id="element-42" data-mp="c8jwg839p1vm">
                  {step.step}
                </span>
                <span className="text-[0.95rem] font-extrabold text-[var(--brand)]" data-id="element-43" data-mp="l8fcorbojn2i">
                  {step.title}
                </span>
              </p>
              <p className="mt-1.5 text-[0.82rem] leading-[1.55] text-[var(--muted)]" data-id="element-44" data-mp="2d8x61obizm6w">
                {step.text}
              </p>
            </li>)}
        </ol>

        <ul className="mt-5 flex flex-wrap gap-2" data-id="element-45" data-mp="2r1mus0ifv57p">
          {SNAK_PAYOFFS.map(payoff => <li key={payoff} className="rounded-full bg-[var(--brand)]/10 px-3 py-1.5 text-[0.8rem] font-bold text-[var(--brand)]" data-id="element-46" data-mp="2m8buki84tmor">
              {payoff}
            </li>)}
        </ul>
      </Section>

      {/* 03 — Why it matters commercially */}
      <Section number="03" title="Why attention pays, phase by phase" icon={TrendingUpIcon} breakBefore data-id="element-47" data-mp="39yekarokqgk7">
        <Paragraph data-id="element-48" data-mp="1fkwx55s2zlpp">
          A brand needs different things at different points in its commercial
          life. The same campaign machine serves all three, with the emphasis
          moved rather than the model rebuilt.
        </Paragraph>

        <dl className="mt-5 space-y-3" data-id="element-49" data-mp="11g68yt3c723v">
          {LIFECYCLE_PRIORITIES.map(priority => <div key={priority.label} className="paper-avoid-break border-l-2 border-[var(--brand)]/40 pl-4" data-id="element-50" data-mp="15gq63wnwu5q0">
              <dt className="text-[0.9rem] font-extrabold leading-snug" data-id="element-51" data-mp="wooacbosddm2">
                {priority.label}
              </dt>
              <dd className="mt-1 text-[0.88rem] leading-[1.6] text-[var(--muted)]" data-id="element-52" data-mp="hqm39rijqa02">
                {priority.text}
              </dd>
            </div>)}
        </dl>

        <h3 className="mt-6 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-[var(--brand)]" data-id="element-53" data-mp="8r8ul128xpge">
          Format follows familiarity
        </h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-54" data-mp="dqbz8kebdtnu">
          {FORMAT_LOGIC.map(item => <div key={item.label} className="paper-avoid-break rounded-xl bg-[var(--surface)] p-4" data-id="element-55" data-mp="232x9524kfq09">
              <p className="text-[0.85rem] font-extrabold leading-snug" data-id="element-56" data-mp="3gp20677xzu9j">
                {item.label}
              </p>
              <p className="mt-1.5 text-[0.82rem] leading-[1.55] text-[var(--muted)]" data-id="element-57" data-mp="3bqgz65kv96k1">
                {item.text}
              </p>
            </div>)}
        </div>
      </Section>

      {/* 04 — The economics */}
      <Section number="04" title="Built once, reused everywhere" icon={CoinsIcon} data-id="element-58" data-mp="32wviggp55f7z">
        <Paragraph data-id="element-59" data-mp="2xyu243j3nu07">
          The reason continued campaigns have historically been unaffordable is
          that every activation was treated as a new production. The Snak model
          separates the master build from the campaign, so cost per campaign
          falls as brands, frequency and markets increase.
        </Paragraph>

        <dl className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-60" data-mp="2k0m7415mj2ro">
          {ECONOMICS_POINTS.map(point => <div key={point.label} className="paper-avoid-break rounded-xl border border-black/10 p-4" data-id="element-61" data-mp="i764s706bzgv">
              <dt className="text-[0.85rem] font-extrabold leading-snug" data-id="element-62" data-mp="2040205ta1icd">
                {point.label}
              </dt>
              <dd className="mt-1.5 text-[0.82rem] leading-[1.55] text-[var(--muted)]" data-id="element-63" data-mp="251hyuxu0s740">
                {point.text}
              </dd>
            </div>)}
        </dl>

        <div className="paper-avoid-break mt-5 rounded-2xl bg-[var(--brand)] p-[clamp(1.25rem,2.5vw,1.75rem)] text-white" data-id="element-64" data-mp="2dvemkh2u1rlj">
          <p className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-white/70" data-id="element-65" data-mp="1h7qc8l90j3cj">
            What it is worth
          </p>
          {VALUE_LOGIC.map(item => <div key={item.label} className="mt-3" data-id="element-66" data-mp="1q4sewqwqq9c2">
              <p className="text-[0.95rem] font-extrabold leading-snug" data-id="element-67" data-mp="1v5y6umiktskj">
                {item.label}
              </p>
              <p className="mt-1 text-[0.85rem] leading-[1.6] text-white/80" data-id="element-68" data-mp="31kh0bducj0ar">
                {item.text}
              </p>
            </div>)}
        </div>
      </Section>

      {/* 05 — How it is run */}
      <Section number="05" title="Not just making campaigns. Running them." icon={EyeIcon} breakBefore data-id="element-69" data-mp="1z5fhgyqzjtt0">
        <Paragraph data-id="element-70" data-mp="28w1po7wdhi1o">
          Snakker works as Snak agency of record: strategy, build, localization,
          distribution and measurement sit with one partner, across brands,
          countries and time. Brand teams keep approval and ownership; the
          running of the calendar does not land on them.
        </Paragraph>

        <h3 className="mt-5 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-[var(--brand)]" data-id="element-71" data-mp="1l5nwuf5yrf61">
          One governance view
        </h3>
        <ul className="mt-3 space-y-2" data-id="element-72" data-mp="1q48zsmclocy6">
          {GOVERNANCE_POINTS.map(point => <li key={point} className="flex gap-3" data-id="element-73" data-mp="1u8oyo3nsqk3w">
              <span className="mt-[0.55rem] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand)]" data-id="element-74" data-mp="31l8nf4o063lv" />
              <span className="text-[0.88rem] leading-[1.6] text-[var(--fg)]/85" data-id="element-75" data-mp="35lrfmztt5bol">
                {point}
              </span>
            </li>)}
        </ul>
        <p className="mt-3 text-[0.8rem] italic leading-[1.55] text-[var(--muted)]" data-id="element-76" data-mp="u0s0w5z8vfx2">
          Every status, session and completion figure is computed from the live
          builds — never editorial.
        </p>

        <dl className="paper-avoid-break mt-6 grid grid-cols-2 gap-3 rounded-2xl bg-[var(--surface)] p-4 sm:grid-cols-4" data-id="element-77" data-mp="g7smy8f41trm">
          {OPERATING_PROOF.map(metric => <div key={metric.label} data-id="element-78" data-mp="b6d0qd175h7n">
              <dt className="text-[1.6rem] font-extrabold leading-none text-[var(--brand)]" data-id="element-79" data-mp="68v2phopwmef">
                {metric.value}
              </dt>
              <dd className="mt-1.5 text-[0.62rem] font-bold uppercase leading-tight tracking-[0.1em] text-[var(--muted)]" data-id="element-80" data-mp="28b409ohxaq6">
                {metric.label}
              </dd>
            </div>)}
        </dl>
      </Section>

      {/* 06 — Next steps */}
      <Section number="06" title="How a first engagement starts" icon={CheckIcon} data-id="element-81" data-mp="1rm6a1b21i56l">
        <ol className="mt-2 space-y-3" data-id="element-82" data-mp="2arkt4ki4vprx">
          {NEXT_STEPS.map(step => <li key={step.step} className="paper-avoid-break flex gap-4" data-id="element-83" data-mp="2aphqn0zso46b">
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-[0.7rem] font-extrabold text-white" data-id="element-84" data-mp="2fjc8rzzr4b3a">
                {step.step}
              </span>
              <div className="min-w-0" data-id="element-85" data-mp="26pzb6kqqslav">
                <p className="text-[0.95rem] font-extrabold leading-snug" data-id="element-86" data-mp="2ues7wjf6ux6j">
                  {step.title}
                </p>
                <p className="mt-1 text-[0.88rem] leading-[1.6] text-[var(--muted)]" data-id="element-87" data-mp="2yfvjuuu4892h">
                  {step.text}
                </p>
              </div>
            </li>)}
        </ol>
      </Section>

      <footer className="paper-avoid-break mt-10 border-t border-black/10 pt-5" data-id="element-88" data-mp="2penp2mqt5qd5">
        <p className="text-[0.72rem] leading-[1.6] text-[var(--muted)]" data-id="element-89" data-mp="190xj1ag4os0v">
          {FOOTNOTE}
        </p>
        <p className="mt-3 text-[0.7rem] font-bold uppercase tracking-[0.16em] text-[var(--brand)]" data-id="element-90" data-mp="1e1v01jpejg0s">
          Snakker · Snak agency of record
        </p>
      </footer>
    </article>;
}